import clsx from 'clsx'
import { createRoot } from 'react-dom/client'
import { useEffect, useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js'
import { BrowserRouter, NavLink, NavLinkRenderProps, Route, Routes } from 'react-router'
import './tailwind.css'
import { Login } from './Login'
import { Monitor } from './Monitor'
import { Statistics } from './Statistics'
import { tokenUtils } from './utils/token-utils'
import { useWebSocket } from './utils/use-websocket'

ChartJS.register(CategoryScale, Filler, Legend, LinearScale, LineElement, PointElement, Tooltip)

ChartJS.defaults.locale = 'fi-FI'

export const App = () => {
  const { connectionStatus, repoWorkflows, connect, disconnect } = useWebSocket()
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | undefined>(undefined)

  useEffect(() => {
    const savedToken = tokenUtils.getValidToken()
    if (savedToken) {
      setToken(savedToken)
      connect(savedToken)
    }
  }, [connect])

  useEffect(() => {
    if (connectionStatus === 'unauthorized') {
      setError('Session expired, please login again')
      setToken(null)
      tokenUtils.removeToken()
    } else if (connectionStatus === 'error') {
      setError('Connection failed after multiple attempts. Check your network connection.')
    } else {
      setError(undefined)
    }
  }, [connectionStatus])

  const handleLogin = (newToken: string) => {
    setToken(newToken)
    setError(undefined)
    connect(newToken)
  }

  const handleLogout = () => {
    disconnect()
    setToken(null)
    tokenUtils.removeToken()
    setError(undefined)
  }

  if (!token) {
    return <Login onLogin={handleLogin} returnError={error} />
  }

  return (
    <div className="mx-8 my-8">
      <div className="flex justify-between items-center mb-4">
        <div
          className={clsx('text-sm', {
            'text-status-success': connectionStatus === 'connected',
            'text-status-failure':
              connectionStatus === 'disconnected' ||
              connectionStatus === 'error' ||
              connectionStatus === 'unauthorized',
            'text-status-attention': connectionStatus === 'connecting',
          })}
        >
          Status: {connectionStatus}
          {connectionStatus === 'connecting' && ' (reconnecting...)'}
        </div>
        <button onClick={handleLogout} className="text-sm text-red-600 hover:text-red-800 underline">
          Logout
        </button>
      </div>

      <div className="mb-4 border-b flex gap-2">
        <NavLink to="/" end className={getTabClassName}>
          Actions
        </NavLink>
        <NavLink to="/statistics" className={getTabClassName}>
          Statistics
        </NavLink>
      </div>

      <Routes>
        <Route path="/" element={<Monitor repoWorkflows={repoWorkflows} />} />
        <Route path="/statistics" element={<Statistics />} />
      </Routes>
    </div>
  )
}

const rootEl = document.getElementById('monitor-app-root')

if (rootEl) {
  const root = createRoot(rootEl)
  root.render(
    <BrowserRouter>
      <App />
    </BrowserRouter>,
  )
}

function getTabClassName({ isActive }: NavLinkRenderProps) {
  return clsx('px-3 py-2 text-sm font-medium', {
    'border-b-2 border-blue-600 text-blue-700': isActive,
    'text-neutral-600 hover:text-neutral-900': !isActive,
  })
}
